import { FinancialTransactionProps } from "@/types/financialTransaction";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { UpdateFinancialTransactionBtn } from "../Update";
import {
  deleteFinancialTransactionData,
  getShowFinancialTransaction,
  updateFinancialTransactionStatus,
} from "@/Api/financialTransactions";
import { DeleteBtn } from "@/components/Delete";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "@/components/Loaders";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowDown,
  ArrowUp,
  CalendarClock,
  CircleDollarSign,
  Home,
  Tag,
  MoreHorizontal,
  ChevronDown,
  PaperclipIcon,
} from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AttachmentModal } from "../AttachmentModal";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { MultiSelectActions } from "../MultiSelectActions";
import { Checkbox } from "@/components/ui/checkbox";
import { usePermissions } from "@/hooks/usePermissions";

interface CardViewProps {
  financialTransactions: FinancialTransactionProps[];
  updateFront: React.Dispatch<
    React.SetStateAction<ApiDataProps<FinancialTransactionProps>>
  >;
  dataApi: ApiDataProps<FinancialTransactionProps>;
  pageActions: TypePageActions;
}

const TRANSACTION_TYPES = {
  ACCOUNTS_PAYABLE: 1,
  ACCOUNTS_RECEIVABLE: 2,
};

const STATUS_COLORS = {
  1: "bg-yellow-500", // Aberto
  2: "bg-green-500", // Pago
  3: "bg-red-500", // Cancelado
};

export function CardView({
  financialTransactions,
  updateFront,
  dataApi,
  pageActions,
}: CardViewProps) {
  const [openPaymentDateDialog, setOpenPaymentDateDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<FinancialTransactionProps | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date | undefined>(new Date());
  const [loading, setLoading] = useState(false);
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [selectedTransactionUuid, setSelectedTransactionUuid] = useState<string>("");
  const { hasPermission } = usePermissions();

  // Hook para seleção múltipla
  const multiSelect = useMultiSelect<FinancialTransactionProps>(
    (item) => item.uuid
  );

  const handleOpenAttachments = (uuid: string) => {
    setSelectedTransactionUuid(uuid);
    setOpenAttachmentModal(true);
  };

  const formatDate = (date: string) => {
    if (!date) return "N/A";
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch (error) {
      return date || "N/A";
    }
  };

  const formatDateOnlyDate = (date: string) => {
    if (!date) return "-";
    try {
      const parsedDate = new Date(date);
      return format(parsedDate, "dd/MM/yyyy");
    } catch (error) {
      return date || "-";
    }
  };


  // Agrupar transações por tipo (a pagar ou a receber)
  const payableTransactions = financialTransactions?.filter(
    (t) =>
      t.financial_transaction_type_id === TRANSACTION_TYPES.ACCOUNTS_PAYABLE
  );

  const receivableTransactions = financialTransactions?.filter(
    (t) =>
      t.financial_transaction_type_id === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE
  );

  const handleStatusChange = async (
    transaction: FinancialTransactionProps,
    newStatusId: number
  ) => {
    if (newStatusId === 2) {
      // Se o status for "Pago", abre o diálogo para seleção da data
      setSelectedTransaction(transaction);
      setPaymentDate(new Date());
      setOpenPaymentDateDialog(true);
    } else {
      await updateTransactionStatus(transaction, newStatusId);
    }
  };

  const updateTransactionStatus = async (
    transaction: FinancialTransactionProps,
    newStatusId: number,
    paymentDate?: Date
  ) => {
    setLoading(true);
    try {
      const payload: any = {
        financial_transaction_status_id: newStatusId,
      };

      if (paymentDate && newStatusId === 2) {
        payload.payment_date = format(paymentDate, "yyyy-MM-dd");
      }

      const { data } = await updateFinancialTransactionStatus(
        transaction.uuid,
        payload
      );

      if (data.status) {
        const updatedData = {
          ...dataApi,
          data: dataApi.data.map((item) =>
            item.uuid === transaction.uuid
              ? {
                  ...data?.response,
                }
              : item
          ), 
        };
        updateFront(updatedData);
        toast.success(`Status atualizado com sucesso!`);
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
    } finally {
      setLoading(false);
      setOpenPaymentDateDialog(false);
    }
  };

  const confirmPaymentDate = () => {
    if (selectedTransaction && paymentDate) {
      updateTransactionStatus(selectedTransaction, 2, paymentDate);
    }
  };

  const handleSelectAll = () => {
    if (multiSelect.isAllSelected(financialTransactions)) {
      multiSelect.clearSelection();
    } else {
      multiSelect.selectAll(financialTransactions);
    }
  };

  // Renderiza um card para uma transação
  const renderTransactionCard = (transaction: FinancialTransactionProps) => {
    const isPayout =
      transaction.financial_transaction_type_id ===
      TRANSACTION_TYPES.ACCOUNTS_PAYABLE;

    return (
      <Card
        key={transaction.id}
        className={cn(
          "w-full transition-all hover:shadow-md",
          isPayout ? "border-red-200" : "border-green-200",
          multiSelect.isSelected(transaction) && "ring-2 ring-blue-500"
        )}
      >
        <CardHeader
          className={cn(
            "pb-2 flex flex-row justify-between items-center",
            isPayout
              ? "bg-red-50 dark:bg-red-900/10"
              : "bg-green-50 dark:bg-green-900/10"
          )}
        >
          <div className="flex items-center gap-3">
            {hasPermission("delete-transaction") && (
              <Checkbox
                checked={multiSelect.isSelected(transaction)}
                onCheckedChange={() => multiSelect.toggleItem(transaction)}
                aria-label={`Selecionar transação ${transaction.description}`}
              />
            )}
            <div className="flex items-center gap-2">
              {isPayout ? (
                <ArrowUp className="h-5 w-5 text-red-500" />
              ) : (
                <ArrowDown className="h-5 w-5 text-green-500" />
              )}
              <h3 className="font-medium text-sm">{transaction.description}</h3>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex items-center gap-1 cursor-pointer focus:outline-none"
              disabled={loading}
            >
              <Badge
                className={cn(
                  STATUS_COLORS[transaction.financial_transaction_status_id] ||
                    "bg-gray-500"
                )}
              >
                {transaction.financial_transaction_status?.name}
              </Badge>
              <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleStatusChange(transaction, 1)}
                className={
                  transaction.financial_transaction_status_id === 1
                    ? "bg-yellow-100 dark:bg-yellow-900/30"
                    : ""
                }
              >
                <Badge className="bg-yellow-500 mr-2">Aberto</Badge>
                <span>Marcar como aberto</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange(transaction, 2)}
                className={
                  transaction.financial_transaction_status_id === 2
                    ? "bg-green-100 dark:bg-green-900/30"
                    : ""
                }
              >
                <Badge className="bg-green-500 mr-2">Pago</Badge>
                <span>Marcar como pago</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange(transaction, 3)}
                className={
                  transaction.financial_transaction_status_id === 3
                    ? "bg-red-100 dark:bg-red-900/30"
                    : ""
                }
              >
                <Badge className="bg-red-500 mr-2">Cancelado</Badge>
                <span>Marcar como cancelado</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>

        <CardContent className="pt-4 pb-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">Valor</span>
              <div
                className={cn(
                  "font-semibold flex items-center gap-1",
                  isPayout ? "text-red-600" : "text-green-600"
                )}
              >
                <CircleDollarSign className="h-4 w-4" />
                {Mask.formatCurrency(transaction.amount)}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">Unidade</span>
              <div className="flex items-center gap-1">
                <Home className="h-4 w-4 text-muted-foreground" />
                {transaction.unity?.name || "-"}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">
                {isPayout ? "Despesa" : "Receita"}
              </span>
              <div className="flex items-center gap-1">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {isPayout
                  ? transaction.expense_type?.name || "-"
                  : transaction.revenue_type?.name || "-"}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">
                Forma de Pagamento
              </span>
              <div className="flex items-center gap-1">
                <CircleDollarSign className="h-4 w-4 text-muted-foreground" />
                {transaction.payment_form?.name || "-"}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-muted-foreground text-xs">
                {transaction.payment_date ? "Pagamento" : "Vencimento"}
              </span>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <CalendarClock className="h-4 w-4 text-muted-foreground" />
                {transaction.payment_date
                  ? formatDate(transaction.payment_date)
                  : transaction.due_date ? formatDateOnlyDate(transaction.due_date) : "-"}
              </div>
            </div>
          </div>
        </CardContent>

        <CardFooter className="pt-2 flex justify-end gap-2">
          <UpdateFinancialTransactionBtn
            uuid={transaction.uuid}
            updateFront={updateFront}
          />
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleOpenAttachments(transaction.uuid)}
          >
            <PaperclipIcon className="h-4 w-4" />
          </Button>
          <DeleteBtn
            params={{
              func: deleteFinancialTransactionData,
              funcParams: transaction.uuid,
            }}
            updateFront={() => {
              const updatedData = {
                ...dataApi,
                data: dataApi.data.filter(
                  (item) => item.uuid !== transaction.uuid
                ),
              };
              updateFront(updatedData);
            }}
            referenceLabel={transaction.description}
          />
          <ShowBtn
            labels={{ title: "Detalhes da Transação" }}
            params={{
              func: getShowFinancialTransaction,
              funcParams: transaction.uuid,
            }}
            render={(data: FinancialTransactionProps) => (
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="text-sm font-medium">Descrição:</h3>
                  <p>{data.description}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Valor:</h3>
                  <p>{Mask.formatCurrency(data.amount)}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Unidade:</h3>
                  <p>{data.unity?.name || "—"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">
                    {data.financial_transaction_type_id ===
                    TRANSACTION_TYPES.ACCOUNTS_PAYABLE
                      ? "Despesa:"
                      : "Receita:"}
                  </h3>
                  <p>
                    {data.financial_transaction_type_id ===
                    TRANSACTION_TYPES.ACCOUNTS_PAYABLE
                      ? data.expense_type?.name || "—"
                      : data.revenue_type?.name || "—"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Forma de Pagamento:</h3>
                  <p>{data.payment_form?.name || "—"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Vencimento:</h3>
                  <p>{data.due_date ? formatDateOnlyDate(data.due_date) : "—"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Data de Competência:</h3>
                  <p>{data.competence_date ? formatDateOnlyDate(data.competence_date) : "—"}</p>
                </div>
                {data.payment_date && (
                  <div>
                    <h3 className="text-sm font-medium">Data de Pagamento:</h3>
                    <p>{formatDate(data.payment_date)}</p>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium">Status:</h3>
                  <Badge
                    className={cn(
                      STATUS_COLORS[data.financial_transaction_status_id] ||
                        "bg-gray-500"
                    )}
                  >
                    {data.financial_transaction_status?.name || "-"}
                  </Badge>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Observação:</h3>
                  <p>{data.observation || "—"}</p>
                </div>
                {data.sale?.transaction_pix_details &&
                 data.sale.transaction_pix_details.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-base font-semibold mb-4 text-indigo-700">
                      🔄 Detalhes das Transações PIX
                    </h3>
                    {data.sale.transaction_pix_details.map((pixDetail, index) => (
                      <div key={pixDetail.id} className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-lg font-semibold text-indigo-800 dark:text-indigo-200">
                            💰 PIX Transação #{index + 1}
                          </h4>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                            Processado
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Informações do Pagador */}
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                            <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                              👤 Dados do Pagador
                            </h5>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Nome:</span>
                                <span className="font-medium text-right max-w-[200px] break-words">{pixDetail.payer_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Documento:</span>
                                <span className="font-mono">{pixDetail.payer_document}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Tipo Doc:</span>
                                <span>{pixDetail.payer_document_type}</span>
                              </div>
                              {pixDetail.payer_email && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Email:</span>
                                  <span className="text-right max-w-[200px] break-words">{pixDetail.payer_email}</span>
                                </div>
                              )}
                              {pixDetail.payer_phone && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Telefone:</span>
                                  <span>{pixDetail.payer_phone}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Informações da Conta */}
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
                            <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                              🏦 Dados Bancários
                            </h5>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Tipo Conta:</span>
                                <span className="uppercase">{pixDetail.payer_account_type}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Agência:</span>
                                <span className="font-mono">{pixDetail.payer_agency}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Conta:</span>
                                <span className="font-mono">{pixDetail.payer_account}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">ISPB:</span>
                                <span className="font-mono text-xs">{pixDetail.payer_ispb}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Informações da Transação */}
                        <div className="mt-4 bg-white dark:bg-gray-800 p-4 rounded-lg border">
                          <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                            📊 Informações da Transação
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-400">Valor:</span>
                                <span className="font-bold text-green-600 text-lg">
                                  {Mask.formatCurrency(parseFloat(pixDetail.amount))}
                                </span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-400">Data/Hora:</span>
                                <span className="font-mono text-xs">
                                  {(() => {
                                    try {
                                      return format(new Date(pixDetail.hour), "dd/MM/yyyy HH:mm:ss");
                                    } catch {
                                      return pixDetail.hour;
                                    }
                                  })()}
                                </span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-400">Chave PIX:</span>
                                <span className="font-mono text-xs max-w-[150px] break-all">{pixDetail.key}</span>
                              </div>
                            </div>
                            <div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-400">TX ID:</span>
                                <span className="font-mono text-xs max-w-[150px] break-all">{pixDetail.tx_id}</span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-400">End to End ID:</span>
                                <span className="font-mono text-xs max-w-[150px] break-all">{pixDetail.end_to_end_id}</span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-400">ID Venda:</span>
                                <span className="font-bold">#{pixDetail.sale_id}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Resumo da Transação */}
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-blue-700 dark:text-blue-300 font-medium">
                              📋 Resumo: Pagamento PIX processado com sucesso
                            </span>
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              ID: {pixDetail.id}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            loader={<CreateLoaderInput lines={8} />}
          />
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Barra de ações para seleção múltipla */}
      {multiSelect.hasSelection && hasPermission("delete-transaction") && (
        <MultiSelectActions
          selectedItems={multiSelect.selectedItems}
          onClearSelection={multiSelect.clearSelection}
          updateFront={updateFront}
        />
      )}

      {/* Botão para selecionar todas as transações */}
      {financialTransactions?.length > 0 && hasPermission("delete-transaction") && (
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            className="flex items-center gap-2"
          >
            <Checkbox
              checked={multiSelect.isAllSelected(financialTransactions)}
              onCheckedChange={() => {}}
            />
            {multiSelect.isAllSelected(financialTransactions)
              ? "Desmarcar Todas"
              : "Selecionar Todas"}
          </Button>
        </div>
      )}

      {financialTransactions?.length > 0 ? (
        <>
          {/* Contas a Receber */}
          {receivableTransactions?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-1 text-green-600">
                <ArrowDown className="h-5 w-5" />
                Contas a Receber
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {receivableTransactions.map(renderTransactionCard)}
              </div>
            </div>
          )}

          {/* Contas a Pagar */}
          {payableTransactions?.length > 0 && (
            <div className={receivableTransactions?.length > 0 ? "mt-8" : ""}>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-1 text-red-600">
                <ArrowUp className="h-5 w-5" />
                Contas a Pagar
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {payableTransactions.map(renderTransactionCard)}
              </div>
            </div>
          )}

          {/* Paginação */}
          <div className="flex justify-between mt-6 mb-3">
            {dataApi?.data.length > 0 && (
              <span className="font-medium">
                Exibindo {dataApi?.current_page} de um total de{" "}
                {dataApi?.last_page}
              </span>
            )}
            {dataApi && dataApi.last_page > 1 && (
              <Pagination className="">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={cn(
                        "h-8 bg-blue-600 hover:bg-blue-700",
                        dataApi.current_page <= 1 &&
                          "pointer-events-none opacity-50"
                      )}
                      onClick={pageActions.previous}
                    />
                  </PaginationItem>

                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className={cn(
                        "size-8 bg-transparent border-2 border-blue-600 text-slate-900 dark:text-white font-bold cursor-auto hover:bg-transparent"
                      )}
                    >
                      <span className="pointer-events-none">
                        {dataApi.current_page}
                      </span>
                    </PaginationLink>
                  </PaginationItem>

                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="text-black dark:text-white h-8 hover:bg-stone-950/10"
                      onClick={pageActions.lastPage}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </PaginationLink>
                  </PaginationItem>

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={cn(
                        "h-8 bg-blue-600 hover:bg-blue-700",
                        dataApi.current_page >= dataApi.last_page &&
                          "pointer-events-none opacity-50"
                      )}
                      onClick={pageActions.next}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          Nenhuma transação encontrada.
        </div>
      )}

      {/* Modal para definir a data de pagamento */}
      <Dialog
        open={openPaymentDateDialog}
        onOpenChange={setOpenPaymentDateDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Data de Pagamento</DialogTitle>
            <DialogDescription>
              Selecione a data em que o pagamento foi realizado.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <Calendar
              mode="single"
              selected={paymentDate}
              onSelect={setPaymentDate}
              className="rounded-md border"
            />
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-between sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setOpenPaymentDateDialog(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              onClick={confirmPaymentDate}
              disabled={!paymentDate || loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? "Processando..." : "Confirmar Pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentModal 
        isOpen={openAttachmentModal}
        onClose={() => setOpenAttachmentModal(false)}
        transactionUuid={selectedTransactionUuid}
      />
    </div>
  );
}
